import { useState, useEffect, useCallback } from "react";
import api, { errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "./DataTable";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export const FieldInput = ({ field, value, onChange }) => {
  if (field.component) {
    const C = field.component;
    return <C value={value} onChange={onChange} />;
  }
  if (field.type === "select")
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(v)}>
        <SelectTrigger data-testid={`form-field-${field.name}`} className="rounded-none">
          <SelectValue placeholder={field.placeholder || "Seçin"} />
        </SelectTrigger>
        <SelectContent>
          {(field.options || []).map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  if (field.type === "textarea")
    return <Textarea data-testid={`form-field-${field.name}`} className="rounded-none" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "checkbox")
    return (
      <div className="flex h-10 items-center">
        <Checkbox data-testid={`form-field-${field.name}`} checked={Boolean(value)} onCheckedChange={(v) => onChange(Boolean(v))} />
      </div>
    );
  if (field.type === "color")
    return (
      <input
        data-testid={`form-field-${field.name}`}
        type="color"
        className="h-10 w-full cursor-pointer border border-gray-200"
        value={value || "#0B1F3A"}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  return (
    <Input
      data-testid={`form-field-${field.name}`}
      className="rounded-none"
      type={field.type || "text"}
      step={field.type === "number" ? "any" : undefined}
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? e.target.value : e.target.value)}
      placeholder={field.placeholder}
    />
  );
};

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
    <div>
      <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#0B1F3A]">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-3">{children}</div>
  </div>
);

export default function CrudPage({
  title, subtitle, module, endpoint, columns, fields,
  defaultValues = {}, filters = [], rowActions, addLabel = "Yeni Ekle",
  transformSubmit, topContent, onChange,
}) {
  const { hasPerm } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultValues);
  const [deleteRow, setDeleteRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(endpoint, { params: { page, search, limit: 15, ...filterVals } });
      setData(d);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, page, search, filterVals]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(defaultValues); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm(row); setDialogOpen(true); };

  const save = async () => {
    for (const f of fields) {
      if (f.required && !form[f.name] && form[f.name] !== 0 && form[f.name] !== false) {
        toast.error(`${f.label} alanı zorunludur`);
        return;
      }
    }
    setSaving(true);
    try {
      let payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0; });
      if (transformSubmit) payload = transformSubmit(payload);
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Kayıt güncellendi" : "Kayıt oluşturuldu");
      setDialogOpen(false);
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${endpoint}/${deleteRow.id}`);
      toast.success("Kayıt silindi");
      setDeleteRow(null);
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        {hasPerm(module, "create") && (
          <Button data-testid={`${module}-add-button`} onClick={openCreate}
            className="rounded-none bg-[#D4AF37] font-semibold text-[#0B1F3A] hover:bg-[#c6a230]">
            <Plus size={18} className="mr-1" /> {addLabel}
          </Button>
        )}
      </PageHeader>

      {topContent}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput); }}
          className="flex items-center gap-2"
        >
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid={`${module}-search-input`}
              className="w-64 rounded-none pl-9"
              placeholder="Ara..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <Button type="submit" variant="outline" className="rounded-none">Ara</Button>
        </form>
        {filters.map((f) => (
          <Select key={f.name} value={filterVals[f.name] || "all"}
            onValueChange={(v) => { setPage(1); setFilterVals({ ...filterVals, [f.name]: v === "all" ? "" : v }); }}>
            <SelectTrigger data-testid={`${module}-filter-${f.name}`} className="w-44 rounded-none">
              <SelectValue placeholder={f.label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{f.label}: Tümü</SelectItem>
              {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
      </div>

      <DataTable
        testId={`${module}-table`}
        columns={columns}
        data={data.items}
        loading={loading}
        page={page}
        pages={data.pages}
        total={data.total}
        onPage={setPage}
        actions={(row) => (
          <>
            {rowActions && rowActions(row, load)}
            {hasPerm(module, "edit") && (
              <button data-testid={`${module}-edit-${row.id}`} onClick={() => openEdit(row)}
                className="flex h-8 w-8 items-center justify-center border border-gray-200 text-[#0B1F3A] transition-colors duration-200 hover:border-[#D4AF37]">
                <Pencil size={14} />
              </button>
            )}
            {hasPerm(module, "delete") && (
              <button data-testid={`${module}-delete-${row.id}`} onClick={() => setDeleteRow(row)}
                className="flex h-8 w-8 items-center justify-center border border-gray-200 text-red-600 transition-colors duration-200 hover:border-red-400">
                <Trash2 size={14} />
              </button>
            )}
          </>
        )}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `${title} Düzenle` : `${title} Ekle`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.colSpan === 2 || f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                </Label>
                <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-end gap-3">
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Vazgeç</Button>
            <Button data-testid={`${module}-save-button`} disabled={saving} onClick={save}
              className="rounded-none bg-[#0B1F3A] text-white hover:bg-[#132c4f]">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteRow)} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Kaydı silmek istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid={`${module}-confirm-delete`} onClick={doDelete}
              className="rounded-none bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
